import React, { useState } from 'react'
import { Icon, Label } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { getAssetStatusColor } from '../../constants'
import { statusMap } from '../common/helpers'
import { formatDateForDisplay } from '../../utils/formatDate'
import { formatCost, problemCost, sumMaintenanceCost } from './assetCostUtils'
import './AssetCard.css'

// ─── ProblemRow ───────────────────────────────────────────────────────────────

const ProblemRow = ({ assetId, problem }) => {
  const fixDate = problem.actualFixDate ?? problem.estimatedFixDate
  const fixDateLabel = problem.actualFixDate ? 'Fix date' : 'Est. fix date'
  const cost = problemCost(problem)
  const costLabel = problem.actualCost != null ? 'Final cost' : 'Est. cost'

  return (
    <Link
      to={`/assets/${assetId}/problems/${problem.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`ac-problem-row${!problem.resolved ? ' ac-problem-row--open' : ''}`}
    >
      <div className="ac-problem-row__top">
        <span className="ac-problem-row__summary">{statusMap[problem.summary] ?? problem.summary}</span>
        {!problem.resolved && <Label color="orange" size="mini">Open</Label>}
        {problem.redTag && <Label color="red" size="mini">Red Tag</Label>}
        <Icon name="angle right" className="ac-problem-row__chevron" />
      </div>


      <div className="ac-problem-row__stats">
        <div className="ac-problem-row__stat">
          <span className="ac-problem-row__stat-label">{fixDateLabel}</span>
          <span className="ac-problem-row__stat-value">{fixDate ? formatDateForDisplay(fixDate) : 'N/A'}</span>
        </div>
        <div className="ac-problem-row__stat ac-problem-row__stat--cost">
          <span className="ac-problem-row__stat-label">{costLabel}</span>
          <span className="ac-problem-row__stat-value">{formatCost(cost)}</span>
        </div>
      </div>

      {problem.notes && (
        <div className="ac-problem-row__notes">
          <span className="ac-problem-row__meta-label">Notes:</span> {problem.notes}
        </div>
      )}

      {problem.resolved && problem.resolution && (
        <div className="ac-problem-row__resolution">
          <Icon name="check circle" color="green" />
          <span><span className="ac-problem-row__meta-label">Resolution:</span> {problem.resolution}</span>
        </div>
      )}

      <div className="ac-problem-row__footer">
        <span>Updated {formatDateForDisplay(problem.modifiedTime)}</span>
      </div>
    </Link>
  )
}


// ─── AssetCard ────────────────────────────────────────────────────────────────

export const AssetCard = ({ asset }) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  const openProblems = asset.openProblems ?? []
  const openProblemIds = new Set(openProblems.map(p => p.id))
  // Exclude problems already shown in the Open Problems section
  const history = (asset.problemHistory ?? []).filter(p => !openProblemIds.has(p.id))
  const hasRedTag = openProblems.some(p => p.redTag)

  return (
    <div className={`ac-card${asset.openStanddown || openProblems.length > 0 ? ' ac-card--alert' : ''}`}>
      <div className="ac-card__header">
        <Link to={`/assets/${asset.id}`} target="_blank" rel="noopener noreferrer" className="ac-card__name">
          <span>{asset.iccName}{asset.unitNumber ? ` (#${asset.unitNumber})` : ''}</span>
          <Icon name="external" />
        </Link>
        <Label color={getAssetStatusColor(asset.status)} size="small">
          {statusMap[asset.status] ?? asset.status ?? 'N/A'}
        </Label>
      </div>

      <div className="ac-card__details">
        {(asset.make || asset.model || asset.year) ? (
          <div className="ac-card__specs">
            {(asset.make || asset.model) && (
              <span className="ac-card__spec ac-card__spec--makemodel">
                {[asset.make, asset.model].filter(Boolean).join(' ')}
              </span>
            )}
            {asset.year && <span className="ac-card__spec ac-card__spec--year">{asset.year}</span>}
          </div>
        ) : (
          <span className="ac-card__spec-empty">No make/model on file</span>
        )}
        {asset.vin && <span className="ac-card__vin">VIN: {asset.vin}</span>}
      </div>

      <div className="ac-card__badges">
        {asset.openStanddown && (
          <Link to={`/standdown/${asset.openStanddownIds?.[0]}`} target="_blank" rel="noopener noreferrer" className="ac-card__badge-link">
            <Label color="red" size="mini">
              <Icon name="tags" />
              <span>Stand-down</span>
              <Icon name="external" />
            </Label>
          </Link>
        )}
        {openProblems.length > 0 && (
          <Link to={`/assets/${asset.id}/problems`} target="_blank" rel="noopener noreferrer" className="ac-card__badge-link">
            <Label color="orange" size="mini">
              <span>{openProblems.length} open problem{openProblems.length !== 1 ? 's' : ''}</span>
              <Icon name="external" />
            </Label>
          </Link>
        )}
        {hasRedTag && <Label color="red" size="mini">Red Tag</Label>}
      </div>

      <div className="ac-card__standdown">
        {asset.lastStanddownDate
          ? `Last stand-down: ${formatDateForDisplay(asset.lastStanddownDate)}`
          : 'No standdown recorded'}
      </div>

      {openProblems.length > 0 && (
        <div className="ac-card__section">
          <Link to={`/assets/${asset.id}/problems`} target="_blank" rel="noopener noreferrer" className="ac-card__section-title">Open Problems</Link>
          {openProblems.map(p => <ProblemRow key={p.id} assetId={asset.id} problem={p} />)}
        </div>
      )}

      {history.length > 0 && (
        <div className="ac-card__section">
          <button type="button" className="ac-card__history-toggle" onClick={() => setHistoryOpen(o => !o)}>
            <Icon name={historyOpen ? 'chevron down' : 'chevron right'} />
            <span>Problem History ({history.length})</span>
            <span className="ac-card__history-toggle-cost">Total cost: {formatCost(sumMaintenanceCost(asset.problemHistory))}</span>
          </button>
          {historyOpen && (
            <div className="ac-card__history-list">
              {history.map(p => <ProblemRow key={p.id} assetId={asset.id} problem={p} />)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
